// The single dropdown this function currently has open, if any. Tracked so a
// newly opened menu can close the previous one without assuming callers share
// a marker class (`.stop` on the click handler blocks the document-level
// globalClick that would otherwise close it).
let openToggle: HTMLElement | null = null;

// Close when focus leaves the toggle + its menu (Tab past the last item, or
// Shift+Tab off the toggle). Safari leaves FocusEvent.relatedTarget null on
// focusout, so we can't read the next target synchronously — defer to the next
// tick and read document.activeElement once focus has settled. The toggle and
// its <ul> are siblings, so the parent bounds the menu without a marker class.
const onFocusOut = () => {
    const $el = openToggle;
    if (!$el) return;
    setTimeout(() => {
        if (openToggle !== $el) return; // already closed / another opened
        if ($el.parentElement?.contains(document.activeElement)) return; // still within the menu
        close($el);
    });
};

// Safari doesn't focus a clicked link/button, so clicking a menu item fires
// focusout (activeElement falls back to body) and the deferred handler hides
// the menu before the item's click lands — the action never runs. Preventing
// the menu's mousedown default keeps focus on the toggle without blocking the
// click, so no premature focusout fires.
const onMouseDown = (event: MouseEvent) => event.preventDefault();

// Close on Escape from anywhere within the toggle + menu, and return focus to
// the toggle per the ARIA APG menu button pattern. stopPropagation keeps Escape
// from also closing an enclosing modal/dialog that happens to be listening too.
const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;
    const $el = openToggle;
    if (!$el) return;
    event.preventDefault();
    event.stopPropagation();
    close($el);
    $el.focus();
};

const close = ($el: HTMLElement) => {
    $el.classList.remove("show");
    $el.setAttribute("aria-expanded", "false");
    $el.parentElement?.removeEventListener("focusout", onFocusOut);
    $el.parentElement?.removeEventListener("mousedown", onMouseDown);
    $el.parentElement?.removeEventListener("keydown", onKeyDown);
    if (openToggle === $el) openToggle = null;
};

export const toggleDropdown = (event: Event) => {
    const $el = event.target! as HTMLElement;
    if (openToggle && openToggle !== $el) {
        close(openToggle);
    }
    if ($el.classList.contains("show")) {
        close($el);
    } else {
        $el.classList.add("show");
        $el.setAttribute("aria-expanded", "true");
        $el.parentElement?.addEventListener("focusout", onFocusOut);
        $el.parentElement?.addEventListener("mousedown", onMouseDown);
        $el.parentElement?.addEventListener("keydown", onKeyDown);
        openToggle = $el;
    }
};
